import { Fragment, useEffect, useState } from "react";
import { UIMatch, useMatches, useNavigate } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Text } from "@design-system/Text/Text";

import classes from "./Breadcrumbs.module.css";

type BreadcrumbLabel = {
  title?: string;
  left?: React.ReactNode;
  /** Reset the breadcrumb to this as the first breadcrumb */
  reset?: boolean;
};

export type Breadcrumb = {
  label: BreadcrumbLabel;
  path?: string;
};

type Handle = {
  breadcrumb?:
    | BreadcrumbLabel
    | ((match: UIMatch) => BreadcrumbLabel | Promise<BreadcrumbLabel>);
};

const Breadcrumbs = () => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const navigate = useNavigate();
  const matches = useMatches() as UIMatch<unknown, Handle>[];

  useEffect(() => {
    (async () => {
      const allBreadcrumbs = await Promise.all(
        matches
          .filter((match) => match.handle?.breadcrumb)
          .map(async (match) => {
            const breadcrumb = match.handle?.breadcrumb;

            return {
              label: (typeof breadcrumb === "function"
                ? await breadcrumb(match)
                : breadcrumb) ?? {
                title: "",
                left: undefined,
              },
              // Remove trailing slash if there is
              path: match.pathname.endsWith("/")
                ? match.pathname.slice(0, -1)
                : match.pathname,
            };
          }),
      );

      // Find the last reset breadcrumb index
      const lastResetIndex =
        allBreadcrumbs
          .map((bc, index) => ({ bc, index }))
          .reverse()
          .find(({ bc }) => bc.label.reset)?.index ?? -1;

      // If we found a reset breadcrumb, only include breadcrumbs from that point onwards
      const finalBreadcrumbs =
        lastResetIndex >= 0
          ? allBreadcrumbs.slice(lastResetIndex)
          : allBreadcrumbs;

      setBreadcrumbs(finalBreadcrumbs);
    })();
  }, [matches]);

  const isSingleBreadcrumb = breadcrumbs.length === 1;

  const handleClick = (path?: string) => {
    if (path) {
      navigate(path, { viewTransition: true });
    }
  };

  return (
    <div className={classes.breadcrumbs}>
      {isSingleBreadcrumb ? (
        <Box align="center" gap={4}>
          <Text size={21} weight={700} spacing="relaxed">
            {breadcrumbs[0].label.left} {breadcrumbs[0].label.title}
          </Text>
        </Box>
      ) : (
        breadcrumbs?.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          const text = (
            <Text
              size={10}
              weight={700}
              color={breadcrumb.path && !isLast ? "primary" : "textDark"}
              spacing="relaxed"
              uppercase
            >
              {breadcrumb.label.left} {breadcrumb.label.title}
            </Text>
          );

          const content =
            breadcrumb.path && !isLast ? (
              <Clickable
                onClick={() => handleClick(breadcrumb.path)}
                outline="xs"
              >
                <Box align="center" gap={4}>
                  {text}
                </Box>
              </Clickable>
            ) : (
              text
            );

          return (
            <Fragment key={breadcrumb.label.title}>
              {content}
              {!isLast && (
                <Text size={10} weight={600} color="textDark">
                  /
                </Text>
              )}
            </Fragment>
          );
        })
      )}
    </div>
  );
};

export default Breadcrumbs;
